import { runeFor } from '../data/runes';
import { SPELLS } from '../data/spells';
import { clamp } from '../physics/vec2';
import type { RuneDef, SpellDef, SpellId } from '../types';

/** A spell's stats resolved for one upgrade level and rune. */
export type SpellStats = Omit<SpellDef, 'perLevel'> & {
  level: number;
  /** The active rune these stats include; null = the base spell. */
  rune: RuneDef | null;
};

/** Upgraded cooldowns never drop below this, whatever the delta table says. */
export const MIN_SPELL_COOLDOWN_MS = 100;

/**
 * Stats for `id` at `level` with `rune` active: the level-1 `SPELLS` values, then the rune's
 * overrides, then (level − 1) × `perLevel` (the rune's own when it has one, else the spell's).
 * The level is floored and clamped to [1, maxLevel], so level 1 (or anything invalid) with no
 * rune is exactly the base table. A rune for another spell, or an unknown id, is ignored, so a
 * synced string can be passed straight in.
 */
export function spellStats(id: SpellId, level: number, rune?: string | null): SpellStats {
  const { perLevel: basePerLevel, ...spell } = SPELLS[id] as SpellDef;
  const runeDef = runeFor(id, rune);
  const base = runeDef ? { ...spell, ...runeDef.overrides } : spell;
  const perLevel = runeDef?.perLevel ?? basePerLevel;
  const lvl = Number.isFinite(level) ? clamp(Math.floor(level), 1, base.maxLevel) : 1;
  const n = lvl - 1;
  const stats: SpellStats = {
    ...base,
    level: lvl,
    rune: runeDef,
    damage: Math.max(0, base.damage + n * (perLevel.damage ?? 0)),
    force: Math.max(0, base.force + n * (perLevel.force ?? 0)),
    range: Math.max(0, base.range + n * (perLevel.range ?? 0)),
    cooldownMs: Math.max(MIN_SPELL_COOLDOWN_MS, base.cooldownMs + n * (perLevel.cooldownMs ?? 0)),
  };
  if (base.durationMs !== undefined) {
    stats.durationMs = Math.max(0, base.durationMs + n * (perLevel.durationMs ?? 0));
  }
  return stats;
}
